import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Button,
    CircularProgress,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Avatar,
    Grid,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { ArrowBack, Notes, Circle, LocalShipping, Receipt, Podcasts, NotificationsActive } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import api from "../../api/axiosInstance";
import userService from "../../services/userService";
import shipmentService from "../../services/shipmentService";
import invoiceService from "../../services/invoiceService";
import presenceService from "../../services/presenceService";
import PageHeader from "../../components/common/PageHeader";
import StatusChip from "../../components/common/StatusChip";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { formatUsd } from "../../utils/currency";

dayjs.extend(relativeTime);

// Same keys/defaults as the customer's own notification toggles
// (CustomerProfile.jsx web, ProfileScreen.jsx mobile) — lets an admin see
// (and fix) exactly what a customer has switched off themselves, e.g. when
// diagnosing "why didn't this customer get their invoice email."
const NOTIF_ITEMS = [
    { key: "newShipment", label: "New shipment created" },
    { key: "statusChange", label: "Shipment status change" },
    { key: "payment", label: "Payment received / invoice issued" },
    { key: "overdueAlert", label: "Overdue invoice alerts" },
    { key: "dailyDigest", label: "Daily summary digest" },
];
const getNotificationDefaults = (prefs = {}) => ({
    email: Object.fromEntries(NOTIF_ITEMS.map((n) => [n.key, prefs?.email?.[n.key] ?? n.key !== "dailyDigest"])),
    inApp: Object.fromEntries(NOTIF_ITEMS.map((n) => [n.key, prefs?.inApp?.[n.key] ?? n.key !== "dailyDigest"])),
});

const DetailRow = ({ label, value }) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            py: 0.875,
            borderBottom: "1px solid",
            borderColor: "divider",
            "&:last-child": { borderBottom: "none" },
        }}
    >
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={600} textAlign="right" maxWidth={260}>
            {value || "—"}
        </Typography>
    </Box>
);

const StatCard = ({ icon, label, value }) => (
    <Card>
        <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {icon}
            <Box>
                <Typography variant="h6" fontWeight={700}>
                    {value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
            </Box>
        </CardContent>
    </Card>
);

// Full customer picture in one place — profile, TIN(s), live status, notes,
// shipments, invoices/payments, and recent notifications — reached by
// clicking a row on Customers & Staff (AdminCustomers.jsx). Follows the
// same stacked-cards / plain-Table convention as AdminVehicleDetail.jsx /
// AdminSailingDetail.jsx rather than tabs or DataGrid.
const AdminCustomerDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const [shipments, setShipments] = useState([]);
    const [shipmentsTotal, setShipmentsTotal] = useState(0);
    const [shipmentsLoading, setShipmentsLoading] = useState(true);

    const [invoices, setInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(true);

    const [liveSession, setLiveSession] = useState(null);

    const [notifications, setNotifications] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(true);

    const [note, setNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);

    const [prefs, setPrefs] = useState(getNotificationDefaults());
    const [savingPrefs, setSavingPrefs] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await userService.getOne(id);
            setUser(data.data.user);
            setNote(data.data.user.notes || "");
            setPrefs(getNotificationDefaults(data.data.user.notificationPreferences));
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to load customer", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        setShipmentsLoading(true);
        shipmentService
            .getAll({ customer: id, limit: 50, sortBy: "updatedAt", sortOrder: "desc" })
            .then(({ data }) => {
                setShipments(data.data || []);
                setShipmentsTotal(data.pagination?.total || 0);
            })
            .catch(() => setShipments([]))
            .finally(() => setShipmentsLoading(false));

        setInvoicesLoading(true);
        invoiceService
            .getAll({ customer: id, limit: 50 })
            .then(({ data }) => setInvoices(data.data || []))
            .catch(() => setInvoices([]))
            .finally(() => setInvoicesLoading(false));

        setNotificationsLoading(true);
        api
            .get(`/notifications/user/${id}`, { params: { limit: 10 } })
            .then(({ data }) => setNotifications(data.data.notifications || []))
            .catch(() => setNotifications([]))
            .finally(() => setNotificationsLoading(false));

        presenceService
            .getLive()
            .then(({ data }) => {
                const match = (data.data.sessions || []).find((s) => s.user?._id === id);
                setLiveSession(match || null);
            })
            .catch(() => {});
    }, [id]);

    const [dismissingDeletion, setDismissingDeletion] = useState(false);
    const dismissDeletionRequest = async () => {
        setDismissingDeletion(true);
        try {
            const { data } = await api.patch(`/users/${id}/dismiss-deletion-request`);
            setUser(data.data.user);
            enqueueSnackbar("Deletion request dismissed", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to dismiss request", { variant: "error" });
        } finally {
            setDismissingDeletion(false);
        }
    };

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const deleteAccount = async () => {
        setDeletingAccount(true);
        try {
            await userService.delete(id);
            enqueueSnackbar("Account deleted", { variant: "success" });
            navigate("/admin/customers");
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to delete account", { variant: "error" });
            setDeletingAccount(false);
        }
    };

    const togglePref = (category, key) =>
        setPrefs((prev) => ({ ...prev, [category]: { ...prev[category], [key]: !prev[category][key] } }));

    const savePrefs = async () => {
        setSavingPrefs(true);
        try {
            const { data } = await userService.update(id, { notificationPreferences: prefs });
            setUser(data.data.user);
            enqueueSnackbar("Notification preferences updated", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to update preferences", { variant: "error" });
            setPrefs(getNotificationDefaults(user.notificationPreferences));
        } finally {
            setSavingPrefs(false);
        }
    };

    const saveNote = async () => {
        setSavingNote(true);
        try {
            const { data } = await userService.update(id, { notes: note });
            setUser(data.data.user);
            enqueueSnackbar("Note saved", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save note", { variant: "error" });
        } finally {
            setSavingNote(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <SkeletonLoader rows={10} />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate("/admin/customers")} sx={{ alignSelf: "flex-start" }}>
                    Back
                </Button>
                <EmptyState icon="👤" title="Customer not found" />
            </Box>
        );
    }

    const outstandingBalance = invoices.reduce((sum, inv) => sum + (inv.balanceDue || 0), 0);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate("/admin/customers")} sx={{ alignSelf: "flex-start" }}>
                Back
            </Button>

            <PageHeader
                title={`${user.firstName} ${user.lastName}`}
                subtitle={user.email}
            />

            {user.deletionRequestedAt && (
                <Alert
                    severity="warning"
                    action={
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                color="error"
                                size="small"
                                onClick={() => setDeleteConfirmOpen(true)}
                                disabled={deletingAccount}
                            >
                                Delete Account
                            </Button>
                            <Button color="inherit" size="small" onClick={dismissDeletionRequest} disabled={dismissingDeletion}>
                                {dismissingDeletion ? "..." : "Dismiss"}
                            </Button>
                        </Box>
                    }
                >
                    This customer requested account deletion on {dayjs(user.deletionRequestedAt).format("MMM D, YYYY h:mm A")}.
                    Delete their account below, or Dismiss if it's already been handled another way.
                </Alert>
            )}

            <Dialog open={deleteConfirmOpen} onClose={() => !deletingAccount && setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete this account?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This permanently deletes <strong>{user.firstName} {user.lastName}</strong>'s account ({user.email}).
                        This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)} disabled={deletingAccount}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={deleteAccount} disabled={deletingAccount}>
                        {deletingAccount ? "Deleting…" : "Delete Account"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<LocalShipping color="primary" />} label="Total Shipments" value={shipmentsTotal} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<Receipt color="primary" />} label="Outstanding Balance" value={formatUsd(outstandingBalance)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard
                        icon={<Podcasts color={liveSession ? "success" : "disabled"} />}
                        label={liveSession ? `Online — ${liveSession.pageLabel || liveSession.page}` : "Offline"}
                        value={liveSession ? dayjs(liveSession.lastSeenAt).fromNow() : "—"}
                    />
                </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start" }}>
                <Box sx={{ flex: 2, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <Card>
                        <CardHeader title={<Typography fontWeight={700} fontSize={14}>Shipments</Typography>} />
                        <CardContent sx={{ pt: 0 }}>
                            {shipmentsLoading ? (
                                <SkeletonLoader rows={4} />
                            ) : shipments.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No shipments on file.
                                </Typography>
                            ) : (
                                <Box sx={{ overflowX: "auto" }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Tracking #</TableCell>
                                                <TableCell>Vendor</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Updated</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {shipments.map((s) => (
                                                <TableRow
                                                    key={s._id}
                                                    hover
                                                    sx={{ cursor: "pointer" }}
                                                    onClick={() => navigate(`/admin/shipments/${s._id}`)}
                                                >
                                                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{s.trackingNumber}</TableCell>
                                                    <TableCell sx={{ fontSize: 12.5 }}>{s.vendor?.name || "—"}</TableCell>
                                                    <TableCell>
                                                        <StatusChip status={s.status} domain="shipment" />
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                                        {dayjs(s.updatedAt).format("MMM D, YYYY")}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader title={<Typography fontWeight={700} fontSize={14}>Invoices & Payments</Typography>} />
                        <CardContent sx={{ pt: 0 }}>
                            {invoicesLoading ? (
                                <SkeletonLoader rows={4} />
                            ) : invoices.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No invoices on file.
                                </Typography>
                            ) : (
                                <Box sx={{ overflowX: "auto" }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Invoice #</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Balance Due</TableCell>
                                                <TableCell>Due Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {invoices.map((inv) => (
                                                <TableRow
                                                    key={inv._id}
                                                    hover
                                                    sx={{ cursor: "pointer" }}
                                                    onClick={() => navigate(`/admin/invoices/${inv._id}/print`)}
                                                >
                                                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{inv.invoiceNumber}</TableCell>
                                                    <TableCell>
                                                        <StatusChip status={inv.status} domain="invoice" />
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12.5 }}>{formatUsd(inv.balanceDue)}</TableCell>
                                                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                                        {inv.dueDate ? dayjs(inv.dueDate).format("MMM D, YYYY") : "—"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader title={<Typography fontWeight={700} fontSize={14}>Recent Notifications</Typography>} />
                        <CardContent sx={{ pt: 0 }}>
                            {notificationsLoading ? (
                                <SkeletonLoader rows={3} />
                            ) : notifications.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No notifications sent yet.
                                </Typography>
                            ) : (
                                notifications.map((n) => (
                                    <Box
                                        key={n._id}
                                        sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: "none" } }}
                                    >
                                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {n.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                                                {dayjs(n.createdAt).fromNow()}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {n.message}
                                        </Typography>
                                    </Box>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </Box>

                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: { xs: "100%", md: 300 } }}>
                    <Card>
                        <CardHeader
                            avatar={
                                <Avatar src={user.profilePhotoUrl} sx={{ bgcolor: "primary.main" }}>
                                    {user.firstName?.[0]}
                                    {user.lastName?.[0]}
                                </Avatar>
                            }
                            title={<Typography fontWeight={700} fontSize={14}>Profile</Typography>}
                        />
                        <CardContent sx={{ pt: 0 }}>
                            <DetailRow label="Phone" value={user.phone} />
                            <DetailRow label="Role" value={<Chip size="small" label={user.role} sx={{ textTransform: "capitalize" }} />} />
                            <DetailRow
                                label="Status"
                                value={
                                    <Chip
                                        size="small"
                                        label={user.isActive ? "Active" : "Disabled"}
                                        sx={{
                                            bgcolor: user.isActive ? "#C6F6D5" : "#FFF5F5",
                                            color: user.isActive ? "#22543D" : "#C53030",
                                        }}
                                    />
                                }
                            />
                            <DetailRow label="Verified" value={user.isVerified ? "Yes" : "No"} />
                            <DetailRow label="Joined" value={dayjs(user.createdAt).format("MMM D, YYYY")} />
                            <DetailRow label="Last Login" value={user.lastLogin ? dayjs(user.lastLogin).format("MMM D, YYYY h:mm A") : "Never"} />
                            <DetailRow
                                label="Address"
                                value={
                                    user.address?.street
                                        ? `${user.address.street}, ${user.address.district || ""} ${user.address.island || ""}`.trim()
                                        : null
                                }
                            />
                            {user.canManageMultipleTins ? (
                                <DetailRow
                                    label="TINs"
                                    value={(user.tins || []).map((t) => (t.label ? `${t.tin} (${t.label})` : t.tin)).join(", ") || "None on file"}
                                />
                            ) : (
                                <DetailRow label="TIN" value={user.tin} />
                            )}
                            <DetailRow label="Waypoint Imports Agent" value={user.isWaypointImportsAgent ? "Yes" : "No"} />
                            <DetailRow label="Bulk CSV Import" value={user.canBulkImportShipments ? "Enabled" : "Disabled"} />
                            <DetailRow label="Bulk Add (Manual Entry)" value={user.canBulkAddShipments ? "Enabled" : "Disabled"} />
                            <DetailRow label="House Account" value={user.isHouseAccount ? "Yes" : "No"} />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader
                            title={<Typography fontWeight={700} fontSize={14}>Notification Preferences</Typography>}
                            avatar={<NotificationsActive sx={{ color: "text.secondary", fontSize: 18 }} />}
                            sx={{ pb: 0 }}
                        />
                        <CardContent sx={{ pt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                What this customer has switched on/off themselves — flip a toggle to fix it for them.
                            </Typography>
                            <Typography variant="overline" color="text.secondary" sx={{ fontSize: 10.5, fontWeight: 700 }}>
                                Email
                            </Typography>
                            {NOTIF_ITEMS.map((n) => (
                                <FormControlLabel
                                    key={`email-${n.key}`}
                                    sx={{ display: "flex", justifyContent: "space-between", ml: 0, width: "100%" }}
                                    labelPlacement="start"
                                    control={
                                        <Switch
                                            size="small"
                                            checked={prefs.email[n.key]}
                                            onChange={() => togglePref("email", n.key)}
                                        />
                                    }
                                    label={<Typography variant="body2">{n.label}</Typography>}
                                />
                            ))}
                            <Typography variant="overline" color="text.secondary" sx={{ fontSize: 10.5, fontWeight: 700, display: "block", mt: 1 }}>
                                In-App
                            </Typography>
                            {NOTIF_ITEMS.map((n) => (
                                <FormControlLabel
                                    key={`inapp-${n.key}`}
                                    sx={{ display: "flex", justifyContent: "space-between", ml: 0, width: "100%" }}
                                    labelPlacement="start"
                                    control={
                                        <Switch
                                            size="small"
                                            checked={prefs.inApp[n.key]}
                                            onChange={() => togglePref("inApp", n.key)}
                                        />
                                    }
                                    label={<Typography variant="body2">{n.label}</Typography>}
                                />
                            ))}
                            <Button size="small" variant="contained" fullWidth onClick={savePrefs} disabled={savingPrefs} sx={{ mt: 1.5 }}>
                                {savingPrefs ? <CircularProgress size={16} color="inherit" /> : "Save Preferences"}
                            </Button>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader
                            title={<Typography fontWeight={700} fontSize={13}>Internal Notes</Typography>}
                            avatar={<Notes sx={{ color: "text.secondary", fontSize: 18 }} />}
                            sx={{ pb: 1 }}
                        />
                        <CardContent sx={{ pt: 0 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Add internal notes visible only to staff…"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                size="small"
                                sx={{ mb: 1.5 }}
                            />
                            <Button size="small" variant="contained" fullWidth onClick={saveNote} disabled={savingNote}>
                                {savingNote ? <CircularProgress size={16} color="inherit" /> : "Save Note"}
                            </Button>
                        </CardContent>
                    </Card>

                    {liveSession && (
                        <Card>
                            <CardContent sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Circle sx={{ fontSize: 10, color: "success.main" }} />
                                <Typography variant="body2">
                                    Online now — viewing <strong>{liveSession.pageLabel || liveSession.page}</strong>
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default AdminCustomerDetail;
